import type { FC, MouseEvent, SyntheticEvent, RefObject } from "react";
import React, { useRef } from "react";
import classnames from "classnames";
import { useTranslation } from "@litres-libs/i18n";
import { Loader } from "../Loader/Loader";
import styles from "./SearchForm.module.scss";
import { DeleteTextIcon } from "../../../../static/icons/DeleteTextIcon";

interface ISearchFormProps {
  isLoading: boolean;
  onFocus: () => void;
  onChange: (value: string) => void;
  onClick: () => void;
  onSubmit: (event: MouseEvent<HTMLFormElement>) => void;
  inputRef: RefObject<HTMLInputElement>;
  value: string;
  placeholder: string;
  isSearchFocused: boolean;
  setPopoverVisibility: (value: boolean) => void;
}

/* форма поиска для десктопа */
export const SearchForm: FC<ISearchFormProps> = ({
  isLoading,
  onFocus,
  onChange,
  onClick,
  onSubmit,
  inputRef,
  value,
  placeholder,
  isSearchFocused,
  setPopoverVisibility,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const clearInput = (e: SyntheticEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    onChange("");
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  };

  return (
    <form
      action="/search/"
      method="get"
      autoComplete="off"
      onSubmit={onSubmit}
      data-testid="header__search-form--desktop"
      role="search"
      aria-label={t(placeholder)}
      ref={formRef}
    >
      <div
        className={classnames(styles.searchFormWrapperAB, {
          [styles.focused]: isSearchFocused,
        })}
      >
        <input
          className={styles.input}
          maxLength={150}
          placeholder={t(placeholder)}
          onChange={({ target: { value } }): void => {
            onChange(value);
          }}
          onClick={onClick}
          onFocus={onFocus}
          onKeyDown={e => {
            if (e.key === "Enter") {
              setPopoverVisibility(!isSearchFocused);
              formRef.current?.submit();
            }
          }}
          ref={inputRef}
          name="q"
          value={value}
          data-testid="search__input"
          role="combobox"
          aria-controls="suggestionsSearchResult"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={isSearchFocused}
        />

        {!!value && (
          <button
            onClick={clearInput}
            className={classnames(styles.clear, {
              [styles.focused]: isSearchFocused,
            })}
            aria-label={t("Очистить строку поиска")}
          >
            <DeleteTextIcon />
          </button>
        )}

        <button
          className={classnames(styles.btn, {
            [styles.loader]: isLoading,
            [styles.focused]: isSearchFocused,
          })}
          onClick={() => {
            setPopoverVisibility(false);
          }}
          onFocus={() => {
            setPopoverVisibility(false);
          }}
          type="submit"
          data-testid="search__button"
          aria-label={t("Найти {q}", { q: value })}
          aria-description={t("Результаты поиска появятся на странице")}
        >
          {isLoading ? <Loader /> : t("Найти")}
        </button>
      </div>
    </form>
  );
};
